import { Home, Building2, Users, UsersRound, Settings, Briefcase, ShieldCheck } from 'lucide-react';
import type { RoleName } from '@/features/auth/types/auth.types';

export interface NavItem {
  label: string;
  path: string;
  icon: React.ElementType;
  roles?: string[];
}

interface ImpersonationNavTemplate {
  label: string;
  suffix: string;
  icon: React.ElementType;
  adminOnly?: boolean;
}

const impersonationNavTemplate: ImpersonationNavTemplate[] = [
  { label: 'Dashboard', suffix: 'dashboard', icon: Home },
  { label: 'Users', suffix: 'users', icon: UsersRound, adminOnly: true },
  { label: 'Candidates', suffix: 'candidates', icon: Users },
  { label: 'Positions', suffix: 'positions', icon: Briefcase },
  { label: 'Settings', suffix: 'settings', icon: Settings, adminOnly: true },
];

const superAdminNavItems: NavItem[] = [
  { label: 'Dashboard', path: '/', icon: Home },
  { label: 'Companies', path: '/companies', icon: Building2, roles: ['SUPER_ADMIN'] },
  { label: 'Super Admins', path: '/users/super-admins', icon: Users, roles: ['SUPER_ADMIN'] },
  {
    label: 'Inspection',
    path: '/verifications/inspection',
    icon: ShieldCheck,
    roles: ['SUPER_ADMIN'],
  },
  { label: 'Settings', path: '/settings', icon: Settings, roles: ['SUPER_ADMIN'] },
];

const adminNavItems: NavItem[] = [
  { label: 'Dashboard', path: '/agent/dashboard', icon: Home },
  { label: 'Users', path: '/agent/users', icon: UsersRound },
  { label: 'Candidates', path: '/agent/candidates', icon: Users },
  { label: 'Positions', path: '/positions', icon: Briefcase },
  { label: 'Settings', path: '/agent/settings', icon: Settings },
];

const agentNavItems: NavItem[] = [
  { label: 'Dashboard', path: '/agent/dashboard', icon: Home },
  { label: 'Candidates', path: '/agent/candidates', icon: Users },
  { label: 'Positions', path: '/positions', icon: Briefcase },
];

function buildImpersonationNav(companyId: string, includeAdminItems: boolean): NavItem[] {
  return impersonationNavTemplate
    .filter((item) => !item.adminOnly || includeAdminItems)
    .map((item) => ({
      label: item.label,
      path: `/companies/${companyId}/${item.suffix}`,
      icon: item.icon,
    }));
}

function getImpersonationNavItems(companyId: string, activeRole: RoleName | null): NavItem[] {
  if (activeRole === 'ADMIN') {
    return buildImpersonationNav(companyId, true);
  }
  if (activeRole === 'MANAGER' || activeRole === 'AGENT') {
    return buildImpersonationNav(companyId, false);
  }
  return [{ label: 'Dashboard', path: `/companies/${companyId}/dashboard`, icon: Home }];
}

function getCompanyScopedNavItems(activeRole: RoleName | null): NavItem[] | null {
  if (activeRole === 'ADMIN') {
    return adminNavItems;
  }
  if (activeRole === 'MANAGER' || activeRole === 'AGENT') {
    return agentNavItems;
  }
  return null;
}

function getFilteredNavItems(activeRole: RoleName | null): NavItem[] {
  return superAdminNavItems.filter((item) => !item.roles || item.roles.includes(activeRole ?? ''));
}

export function getNavItems(
  isImpersonating: boolean,
  companyId: string | undefined,
  activeRole: RoleName | null
): NavItem[] {
  if (isImpersonating && companyId) {
    return getImpersonationNavItems(companyId, activeRole);
  }

  const companyNav = getCompanyScopedNavItems(activeRole);
  if (companyNav) {
    return companyNav;
  }

  if (activeRole === 'SUPER_ADMIN') {
    return superAdminNavItems;
  }

  return getFilteredNavItems(activeRole);
}
